import { MIC_ERR_LIFECYCLE } from "../../../constants/microphone";
import { AudioStreamFormat } from "../../../models/microphone";

/**
 * Clear AudioContext if applicable
 *
 * @param audioContext To be cleared or suspended AudioContext
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/MicAudioSource.ts#L324
 */
export const clearAudioContext = (
  audioContext?: AudioContext
): AudioContext | undefined => {
  if (audioContext) {
    // This pattern brought to you by a bug in the TypeScript compiler where it
    // confuses the ("close" in this.privContext) with this.privContext always being null as the alternate.
    // https://github.com/Microsoft/TypeScript/issues/11498
    let hasClose = false;
    if ("close" in audioContext) {
      hasClose = true;
    }

    if (hasClose) {
      audioContext.close();
      return undefined;
    } else if (null !== audioContext && audioContext.state === "running") {
      // Suspend actually takes a callback, but analogous to the
      // resume method, it'll be only fired if suspend is called
      // in a direct response to a user action. The later is not always
      // the case, as TurnOff is also called, when we receive an
      // end-of-speech message from the service. So, doing a best effort
      // fire-and-forget here.
      audioContext.suspend();

      return audioContext;
    }
  }
};

/**
 * Must be called before clearing AudioContext
 * @param audioCtx context of the node, required for disconnecting the scriptNode
 * @param scriptNode to be cleared node
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts#L102
 */
export const clearScriptProcessorNode = (
  audioCtx?: AudioContext,
  scriptNode?: ScriptProcessorNode
): undefined => {
  if (audioCtx && scriptNode) {
    scriptNode.disconnect(audioCtx.destination);
  }
  return;
};

/**
 *
 * @param source to be cleared input stream source
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts#L102
 */
export const clearMediaStreamSource = (
  source?: MediaStreamAudioSourceNode
): undefined => {
  if (source) {
    source.disconnect();
  }
  return;
};

/**
 * Instantiate a new AudioContext or return the existing one. This method
 * does not check the AudioContext state!
 *
 * @param prevAudioCtx previously existing AudioContext
 * @throws MIC_ERR_LIFECYCLE.AUDIO_CONTEXT_NOT_AVAILABLE.key
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/MicAudioSource.ts#L306
 */
export const getAudioContext = (prevAudioCtx?: AudioContext): AudioContext => {
  if (prevAudioCtx) {
    return prevAudioCtx;
  }

  const AudioCxt =
    (window as any).AudioContext || (window as any).webkitAudioContext;

  if (!AudioCxt) {
    throw new Error(MIC_ERR_LIFECYCLE.AUDIO_CONTEXT_NOT_AVAILABLE.key);
  }

  const audioContext: AudioContext = new AudioCxt();

  return audioContext;
};

/**
 * Instantiate a ScriptProcessorNode
 *
 * @param audioCtx AudioContext of the audio node
 * @param onaudioprocess New data handler
 * @param streamFormat Expect microphone input strea configuration
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts#L14
 */
export const getScriptProcessorNode = (
  audioCtx: AudioContext,
  onaudioprocess: (ev: AudioProcessingEvent) => void,
  streamFormat: AudioStreamFormat
): ScriptProcessorNode => {
  let bufferSize = 0;
  let scriptNode: ScriptProcessorNode;
  try {
    scriptNode = audioCtx.createScriptProcessor(bufferSize, 1, 1);
  } catch (error) {
    // Webkit (<= version 31) requires a valid bufferSize.
    bufferSize = 2048;
    let sampleRate = audioCtx.sampleRate;
    while (bufferSize < 16384 && sampleRate >= 2 * streamFormat.samplePerSec) {
      bufferSize <<= 1;
      sampleRate >>= 1;
    }
    scriptNode = audioCtx.createScriptProcessor(bufferSize, 1, 1);
  }

  scriptNode.onaudioprocess = onaudioprocess;

  return scriptNode;
};

/**
 * Instantiate a MediaStream source from the default microphone
 * @param audioCtx AudioContext of the audio node
 * @param stream InputStream to create the source from
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts#L49
 */
export const getMediaStreamSource = (
  audioCtx: AudioContext,
  stream: MediaStream
): MediaStreamAudioSourceNode => {
  return audioCtx.createMediaStreamSource(stream);
};
